import React from 'react';
import { useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { KeyboardArrowLeft, KeyboardArrowRight, CalendarToday } from '@mui/icons-material';

import { Typography, IconButton, Button } from '~/UIKit';
import RectangularButton from '../../../../Components/RectangularButton/RectangularButton';
import { useDateFns } from '~/Hooks';
import { BORDER_COLOR_SHADE_2 } from '~/Constants/Colors.constants';
import { makeStyles } from '~/UIKit/Style';
import { FONT_SIZE_LARGE } from '~/Constants/UI.constants';
import { useCalendarNavigation } from './useCalendarNavigation';

const useStyles = makeStyles(
  {
    container: {
      padding: '10px 0',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      borderBottom: `thin solid ${BORDER_COLOR_SHADE_2}`,
      marginBottom: 15,
    },
    text: {
      fontSize: FONT_SIZE_LARGE,
      fontWeight: 'bold',
      lineHeight: '29px',
    },
    weekMonthNavigation: {
      display: 'flex',
      alignItems: 'center',
      textAlign: 'center',
    },
    nextButton: {
      fontSize: FONT_SIZE_LARGE,
    },
    prevButton: {
      fontSize: FONT_SIZE_LARGE,
    },
    toolbarContainer: {
      display: 'flex',
      flexWrap: 'nowrap',
      marginRight: 10,
      alignItems: 'center',
    },
    createEventButton: {
      marginLeft: 20,
    },
  },
  {
    name: 'CalendarNavigation',
  },
);

export interface CalendarNavigationProps {
  focusedDay: Date;
  onTodayClick: () => void;
  onNext: () => void;
  onPrev: () => void;
  toggleCalendarFormat: () => void;
}

export const CalendarNavigation = ({
  focusedDay,
  toggleCalendarFormat,
  onTodayClick,
  onPrev,
  onNext,
}: CalendarNavigationProps) => {
  const { classes } = useStyles();
  const { formatDate } = useDateFns();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { handleCreateEvent, onNextWithTracker, onPrevWithTracker } = useCalendarNavigation({
    focusedDay,
    onPrev,
    onNext,
  });

  return (
    <div className={classes.container} data-testid="CalendarNavigationWrapper">
      <div className={classes.weekMonthNavigation}>
        <IconButton className={classes.prevButton} onClick={onPrevWithTracker} size="large">
          <KeyboardArrowLeft />
        </IconButton>
        <Typography className={classes.text}>
          {formatDate(focusedDay.toISOString(), 'LLL yyyy')}
        </Typography>
        <IconButton className={classes.nextButton} onClick={onNextWithTracker} size="large">
          <KeyboardArrowRight />
        </IconButton>
      </div>
      <div className={classes.toolbarContainer}>
        <IconButton onClick={toggleCalendarFormat} size="large">
          <CalendarToday color="primary" />
        </IconButton>
        <RectangularButton color="primary" onClick={onTodayClick}>
          {t('app.today')}
        </RectangularButton>
        <Button
          onClick={handleCreateEvent}
          variant="contained"
          color="primary"
          size="small"
          data-testid="dashboard-newEvent"
          className={classes.createEventButton}
        >
          {isMobile ? t('app.add') : `+ ${t('event.createNew')}`}
        </Button>
      </div>
    </div>
  );
};
